import React from 'react';
import {
  LayoutDashboard, FolderClosed, Factory,
  LogOut, PanelLeftClose, Pin,
} from 'lucide-react';
import { Avatar, RobbieMark } from '../ui';

/**
 * v2 app sidebar — dark navy chrome rail, ported from the
 * `Dashboard v2.dc.html` mock: logo mark with nav glow, WORKSPACE group,
 * active item with cobalt wash + accent bar, workspace footer (credits chip
 * slot + user card) pinned at the bottom. Purely presentational — the caller
 * wires navigation and fills the footer slot. Width comes from the parent
 * (`w-nav-rail` in AppShell).
 */

export interface ShellNavItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** Count badge (mono, e.g. RFQ count). */
  count?: number;
  /** No page yet: honest non-clickable affordance, no dead ends. */
  soon?: boolean;
}

export interface ShellNavGroup {
  label: string;
  items: ShellNavItem[];
}

export interface AppSidebarProps {
  groups: ShellNavGroup[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  user?: { name: string; role?: string };
  /**
   * Workspace-footer slot directly above the user card — the org credits chip
   * (`CreditsChip`) lives here. A node, not data, so this stays presentational.
   */
  footerSlot?: React.ReactNode;
  /** Sign-out affordance on the user card (not in the mock; kept for parity). */
  onLogout?: () => void;
  /**
   * Pin affordance next to the brand: pinned = docked in flow, unpinned = this
   * is the hover flyout and the control pins it. Omit `onTogglePin` on a
   * sidebar that cannot collapse (the design gallery's static shell).
   */
  pinned?: boolean;
  onTogglePin?: () => void;
  className?: string;
}

/**
 * Product nav per the Dashboard v2 mock. Pages without routes stay `soon`.
 *
 * `ShellPage` routes the ids.
 */
export const defaultNavGroups: ShellNavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { id: 'overview', label: 'Overview', icon: <LayoutDashboard /> },
      { id: 'rfqs', label: 'RFQs', icon: <FolderClosed /> },
      { id: 'suppliers', label: 'Suppliers', icon: <Factory />, soon: true },
      // Datasheets is hidden (27 Sep 2026): an upload writes into the knowledge
      // graph every org reads, so it stays staff-only until uploads are scoped
      // to the org. pages/DatasheetUpload.tsx is kept for that return.
    ],
  },
];

const NavRow: React.FC<{ item: ShellNavItem; active: boolean; onNavigate?: (id: string) => void }> = ({
  item,
  active,
  onNavigate,
}) => {
  const base = 'relative flex w-full items-center gap-s4 rounded-control px-s4 py-[9px] text-left text-[13.5px]';
  const iconSlot = (
    <span className={`inline-flex shrink-0 [&>svg]:h-4 [&>svg]:w-4 ${active ? 'text-[#7FB0F9]' : ''}`}>{item.icon}</span>
  );
  const right = item.count !== undefined ? (
    <span className="ml-auto rounded-pill bg-white/[0.08] px-[7px] py-px font-mono text-[11px] font-semibold text-chrome-text">
      {item.count}
    </span>
  ) : null;

  if (item.soon) {
    return (
      <div className={`${base} cursor-default font-medium text-chrome-text opacity-55`} aria-disabled="true">
        {iconSlot}
        {item.label}
        {right ?? (
          <span className="ml-auto text-[9.5px] font-bold uppercase tracking-[0.12em] text-chrome-label">Soon</span>
        )}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate?.(item.id)}
      aria-current={active ? 'page' : undefined}
      className={`${base} ${
        active
          ? 'bg-chrome-active font-semibold text-white'
          : 'font-medium text-chrome-text hover:bg-white/5 hover:text-white'
      }`}
    >
      {active && <span className="absolute bottom-s3 left-0 top-s3 w-[2.5px] rounded-[2px] bg-pipeline-4" />}
      {iconSlot}
      {item.label}
      {right}
    </button>
  );
};

export const AppSidebar: React.FC<AppSidebarProps> = ({
  groups,
  activeId,
  onNavigate,
  user,
  footerSlot,
  onLogout,
  pinned = false,
  onTogglePin,
  className = '',
}) => (
  <aside className={`flex h-full shrink-0 flex-col bg-chrome px-s5 py-[22px] ${className}`}>
    <div className="mb-[30px] flex items-center gap-s4 px-s3">
      <RobbieMark size="md" glow aria-label="Hello Robbie" />
      <span className="whitespace-nowrap text-[15px] font-bold tracking-[-0.01em] text-white">Hello Robbie</span>
      {onTogglePin && (
        <button
          type="button"
          onClick={onTogglePin}
          aria-label={pinned ? 'Collapse navigation to the rail' : 'Keep navigation open'}
          aria-expanded={pinned}
          aria-pressed={pinned}
          title={pinned ? 'Collapse navigation to the rail' : 'Keep navigation open'}
          className="-mr-s2 ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-control text-chrome-text hover:bg-white/5 hover:text-white"
        >
          {pinned ? <PanelLeftClose className="h-[15px] w-[15px]" /> : <Pin className="h-[15px] w-[15px]" />}
        </button>
      )}
    </div>
    {groups.map((group, i) => (
      <React.Fragment key={group.label}>
        <div
          className={`mb-s3 px-s3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-chrome-label ${
            i > 0 ? 'mt-s7' : ''
          }`}
        >
          {group.label}
        </div>
        <nav className="flex flex-col gap-[2px]">
          {group.items.map((item) => (
            <NavRow key={item.id} item={item} active={item.id === activeId} onNavigate={onNavigate} />
          ))}
        </nav>
      </React.Fragment>
    ))}
    <div className="mt-auto">
      {footerSlot}
      {user && (
        <div className="flex items-center gap-s4 border-t border-white/[0.08] px-s3 pt-[12px]">
          <Avatar name={user.name} tone="action" className="h-[30px] w-[30px] text-[12px]" />
          <div className="flex min-w-0 flex-col">
            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-body-md font-semibold text-white">{user.name}</span>
            {user.role && (
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-meta text-chrome-text">{user.role}</span>
            )}
          </div>
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              aria-label="Sign out"
              title="Sign out"
              className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-control text-chrome-text hover:bg-white/5 hover:text-white"
            >
              <LogOut className="h-[14px] w-[14px]" />
            </button>
          )}
        </div>
      )}
    </div>
  </aside>
);

export default AppSidebar;
